import React, { Fragment, useRef, useEffect } from 'react'
import { useGLTF, PerspectiveCamera } from '@react-three/drei'
import Parts from './Parts/Parts'
import { locations } from './Locations'
import useCalculations from '../utils/useCalculations'
import * as dat from 'dat.gui'

export function Model({ state }) {
  const { nodes, materials } = useGLTF('/main.glb')
  const ref = useRef()

	// useEffect(() => {
	// 	// DEBUG
	// 	const gui = new dat.GUI()
	
	// 	gui.add(ref.current.position, 'x').min(-20).max(20).step(0.001).name('x')
	// 	gui.add(ref.current.position, 'y').min(-20).max(20).step(0.001).name('y')
	// 	gui.add(ref.current.position, 'z').min(-20).max(20).step(0.001).name('z')
	// }, [])

  const { 
    type,
    measures,
    foundation,
    secure,
    sidePurlins,
    cropSupportBars,
    longitudinalBars,
    antiHotSpotTape,
    fadripIrrigation,
    overheadIrrigation,
    gableEnd,
    gableEnd2,
    sideVent,
    gutters,
    color,
    showPlants
  } = state

  const parts = []
  const step = 2
  const liftFoundation = 0.1

  const defaultLengths = {
    "HOBBY": 4000,
    "MID": 6000,
    "COMMERCIAL": 6000
  }

  const widthFix = {
    2500: 0,
    3500: 0.028,
    4500: 0.001,
    5500: 0,
    6000: 0,
    7000: 0,
    8000: 0,
    9000: 0,
  }

  const sideVentFix = {
    3500: 0.01
  }

  const moveIrrigation = {
    2500: 0.02,
    3500: 0.05,
    4500: 0.07,
    5500: 0.09,
    6000: 0.1,
    7000: 0.13,
    8000: 0.16,
    9000: 0.18,
  }

  const scaleGround = {
    2500: 2500,
    3500: 2550,
    4500: 2580,
    5500: 2590,
    6000: 2600,
    7000: 2600,
    8000: 2610,
    9000: 2610,
  }

  const sidePurlinsScaleLength = {
    'HOBBY': 3356.5,
    'MID': 1438.46,
    'COMMERCIAL': 1438.46
  }

  const plants = {
    2500: [1, 2],
    3500: [1, 3],
    4500: [2, 3],
    5500: [2, 4],
    6000: [2, 5],
    7000: [3, 6],
    8000: [3, 7],
    9000: [4, 8],
  }

  const {
    archsNr,
    scale,
    lengthToAdd,
    widthToAdd,
    archsToAdd
  } = useCalculations({ 
    type,
    measures,
    defaultLengths
  })

  const {
    Ground,
    Arch,
    Foundation,
    Concrete,
    FlatConcrete,
    Cross,
    RidgeBar,
    SidePurlin,
    Longitudinal,
    SteelGable,
    SingleTimberGable,
    DoubleTimberGable,
    PVCGable,
    SingleAluExternalGable,
    SingleAluExternalGable_2500,
    SingleAluInternalGable,
    DoubleAluInternalGable,
    SingleSteelGable,
    DoubleSteelGable,
    SteelNoDoorGable,
    HalfMoonGable,
    FadripIrrigation,
    Cover,
    CladdingRailRight,
    CladdingRailLeft,
    EndNoCut,
    SideVent,
    VBar,
    OverheadIrrigation,
    GroundSecure,
    InsideGround,
    Tomato,
    Vegie
  } = Parts({ 
    nodes,
    materials,
    type,
    measures,
    secure,
    foundation,
    archsNr,
    gableEnd,
    sideVent,
    color
  })


  // ARCHS
  for(let i = 0; i < archsNr; i++ ) {
    parts.push(
      <Arch
        index={i}
        key={`arch-${i}`}
        position={[
          locations['ARCH'].position[0],
          locations['ARCH'].position[1] - `${foundation.type === 'FLAT_FEET' ? -liftFoundation : 0}`,
          locations['ARCH'].position[2] + (i * step)
        ]}
        rotation={measures.width === 2500 ? locations['ARCH'].rotation : [0, 0, 0]}
        cropSupportBar={cropSupportBars}
        zigzag={gableEnd.cover === 'CLADDING'}
        archAntiHotSpotTape={(gableEnd.cover === 'CLADDING' && i === 0) || (gableEnd.cover === 'CLADDING' && i === archsNr -1) ? false : antiHotSpotTape}
        antiHotSpotTape={antiHotSpotTape}
      />
    )
  }

  // COVERS
  for(let i = 0; i < archsNr - 1; i++ ) {
    parts.push(
      <Cover
        key={`cover-${i}`}
        position={[
          locations['ARCH'].position[0],
          locations['ARCH'].position[1] - `${foundation.type === 'FLAT_FEET' ? -liftFoundation : 0}`,
          locations['ARCH'].position[2] + (i * step)
        ]}
        rotation={[0, 0, 0]}
        last={i === archsNr - 2}
      />
    )
  }

  // GROUND SECURE
  if(secure === 'GROUND') {
    locations['GROUND-SECURE'].map((gs, index) => {
      for(let a = 0; a < archsNr - 1; a++) {
        parts.push(
          <GroundSecure
            key={`ground-secure-${index}-${a}`}
            position={[
              gs.position[0] - `${index === 1 ? widthToAdd : 0}`,
              gs.position[1],
              gs.position[2] + (a * 2)
            ]}
            rotation={gs.rotation}
          />
        )
      }
    })
  }

  // CROSSES
  if(type === 'HOBBY') {
    for(let i = 0; i < archsNr; i++ ) {
      parts.push(
        <Cross
          index={i}
          key={`cross-${i}`}
          position={[
            locations[measures.width]['CROSS'].position[0],
            locations[measures.width]['CROSS'].position[1] - `${foundation.type === 'FLAT_FEET' ? -liftFoundation : 0}`,
            locations[measures.width]['CROSS'].position[2] + (i * step)
          ]}
        />
      )
    }
  }

  // RIDGE-BAR
  for(let i = 0; i < archsNr - 1; i++ ) {
    parts.push(
      <RidgeBar
        key={`ridge-bar-${i}`}
        position={[
          locations[measures.width]['RIDGE-BAR'].position[0],
          locations[measures.width]['RIDGE-BAR'].position[1] - `${foundation.type === 'FLAT_FEET' ? -liftFoundation : 0}`,
          locations[measures.width]['RIDGE-BAR'].position[2] + (i * step)
        ]}
        antiHotSpotTape={antiHotSpotTape}
      />
    )
  }

  // FOUNDATION
  for(let s = 0; s < 2; s++ ) {
    for(let i = 0; i < archsNr; i++ ) {
      parts.push(
        <Foundation
          key={`foundation-${s}-${i}`}
          position={[
            locations['FOUNDATION'].position[0] - (s * (measures.width / 1000)) + parseFloat(`${ s === 1 ? widthFix[measures.width] : 0}`),
            locations['FOUNDATION'].position[1] - `${foundation.type === 'FLAT_FEET' ? -liftFoundation : 0}`,
            locations['FOUNDATION'].position[2] + (i * step)
          ]}
          rotation={[
            locations['FOUNDATION'].rotation[0],
            s === 0 ? locations['FOUNDATION'].rotation[1] : Math.PI / 2,
            locations['FOUNDATION'].rotation[2]
          ]}
        />
      )
    }
  }

  // CONCRETE
  if(foundation.concrete) {
    for(let s = 0; s < 2; s++ ) {
      for(let i = 0; i < archsNr; i++ ) {
        parts.push(
          <Concrete
            key={`concrete-${s}-${i}`}
            position={[
              locations['FOUNDATION'].position[0] - (s * (measures.width / 1000)) + parseFloat(`${ s === 1 ? widthFix[measures.width] : 0}`),
              locations['FOUNDATION'].position[1],
              locations['FOUNDATION'].position[2] + (i * step)
            ]}
          />
        )
      }
    }
  }

  // FLAT CONCRETE
  if(foundation.type === 'FLAT_FEET') {
    for(let s = 0; s < 2; s++ ) {
      for(let i = 0; i < 2; i++ ) {
        parts.push(
          <FlatConcrete
            key={`flat-concrete-${s}-${i}`}
            position={[
              locations['FLAT_CONCRETE'][s].position[0] - `${s === 0 && i ===  1 ? 2.5 + widthToAdd : 0}`,
              locations['FLAT_CONCRETE'][s].position[1] - `${foundation.type === 'FLAT_FEET' ? -liftFoundation : 0}`,
              locations['FLAT_CONCRETE'][s].position[2] - `${s === 1 && i ===  1 ? -4 - lengthToAdd - `${measures.width > 4500 ? 2 : 0}` : 0}`
            ]}
            rotation={locations['FLAT_CONCRETE'][s].rotation}
            scale={scale(s === 1 ? 2.2 + widthToAdd : 4.3 + lengthToAdd - `${measures.width > 4500 ? -2 : 0}`, 4.3)}
          />
        )
      }
    }
  }

  // SIDE PURLINS
  if(sidePurlins) {
    locations[measures.width]['SIDE-PURLIN'].map((sp, index) => (
      parts.push(
        <SidePurlin
          key={`side-purlin-${index}`}
          position={[
            sp.position[0],
            sp.position[1] - `${foundation.type === 'FLAT_FEET' ? -liftFoundation : 0}`,
            sp.position[2] - `${type === 'HOBBY' && index === 1 ? -lengthToAdd : 0}`
          ]}
          rotation={sp.rotation}
          lengthToAdd={lengthToAdd}
          scale={scale(measures.length - (defaultLengths[type] - sidePurlinsScaleLength[type]), sidePurlinsScaleLength[type])}
          antiHotSpotTape={antiHotSpotTape}
        />
      )
    ))
  }

  // V - BAR
  if(type === 'COMMERCIAL') {
    locations[measures.width]['V-BAR'].map((vb, index) => (
      parts.push(
        <VBar
          key={`v-bar-${index}`}
          position={[
            vb.position[0],
            vb.position[1] - `${foundation.type === 'FLAT_FEET' ? -liftFoundation : 0}`,
            vb.position[2] - `${index === 1 ? -lengthToAdd : 0}`
          ]}
          rotation={vb.rotation}
        />
      )
    ))
  }

  // LONGITUDINAL BARS
  if(cropSupportBars && longitudinalBars) {
    let lbStep = (locations[measures.width]['LONGITUDINAL'][1].position[0] - locations[measures.width]['LONGITUDINAL'][0].position[0]) / (longitudinalBars - 1)
    
    if(longitudinalBars < 2) {
      lbStep = 0
    }

    for(let l = 0; l < longitudinalBars; l++) {
      parts.push(
        <Longitudinal
          key={`longitudinal-${l}`}
          position={[
            locations[measures.width]['LONGITUDINAL'][0].position[0] + (l * lbStep),
            locations[measures.width]['LONGITUDINAL'][0].position[1] - `${foundation.type === 'FLAT_FEET' ? -liftFoundation : 0}`,
            locations[measures.width]['LONGITUDINAL'][0].position[2] - (-(measures.length / 1000) / 2)
          ]}
          scale={scale(measures.length - 2000, 2000)}
          move={(((measures.length - 2000) / 2) - 1000) / 1000}
        />
      )
    }
  }

  // INSIDE GROUND
  for(let ig = 0; ig < archsNr - 1; ig++) {
    parts.push(
      <InsideGround
        key={`inside-ground-${ig}`}
        position={[
          locations['INSIDE-GROUND'].position[0],
          locations['INSIDE-GROUND'].position[1],
          locations['INSIDE-GROUND'].position[2] + (ig * 2) - `${ig === archsNr - 2 ? 0.07 : 0}`
        ]}
        scale={scale(measures.width, scaleGround[measures.width])}
      />
    )
  }

  if(showPlants) {
      // TOMATO
      for(let tlr = 0; tlr < plants[measures.width][0]; tlr++) {
        for(let tl = 0; tl < archsNr - 1; tl++) {
          for(let t = 0; t < 2; t++) {
            parts.push(
              <Tomato
                key={`tomato-${tlr}-${tl}-${t}`}
                position={[
                  locations['TOMATO'].position[0] - 0.55 - (tlr * 0.8),
                  locations['TOMATO'].position[1],
                  locations['TOMATO'].position[2] + 0.5 + (tl * 2) + (t * 0.5) + parseFloat(`${t === 1 ? 0.5 : 0}`)
                ]}
                rotation={[
                  locations['TOMATO'].rotation[0],
                  Math.random() * Math.PI * 2,
                  locations['TOMATO'].rotation[2]
                ]}
              />
            )
          }
        }
      }
      // VEGIE
      for(let vlr = 0; vlr < plants[measures.width][1]; vlr++) {
        for(let vl = 0; vl < archsNr - 1; vl++) {
          for(let v = 0; v < 4; v++) {
            if(vl === archsNr - 2 && v === 3) {
              parts.push()
            } else {
              parts.push(
                <Vegie
                  key={`vegie-${vlr}-${vl}-${v}`}
                  position={[
                    locations['VEGIE'].position[0] - (measures.width / 1000) + 0.23 + (vlr * 0.4),
                    locations['VEGIE'].position[1],
                    locations['VEGIE'].position[2] + 0.5 + (vl * 2) + (v * 0.5)
                  ]}
                  rotation={[
                    locations['VEGIE'].rotation[0],
                    locations['VEGIE'].rotation[1],
                    Math.random() * Math.PI * 2
                  ]}
                />
              )
            }
          }
        }
      }
    }

  


  // PVC GABLE
  if(gableEnd.type === 'PVC') {
    parts.push(
      <PVCGable
        key={`pvc-gable`}
        position={[
          locations['GABLE-END'].position[0],
          locations['GABLE-END'].position[1] - `${foundation.type === 'FLAT_FEET' ? -liftFoundation : 0}`,
          locations['GABLE-END'].position[2]
        ]}
        cover={gableEnd.cover}
      />
    )
  }
  if(gableEnd2.type === 'PVC') {
    parts.push(
      <PVCGable
        key={`pvc-gable-2`}
        position={[
          locations['GABLE-END-BACK'].position[0] - widthToAdd,
          locations['GABLE-END-BACK'].position[1] - `${foundation.type === 'FLAT_FEET' ? -liftFoundation : 0}`,
          locations['GABLE-END-BACK'].position[2] + lengthToAdd - `${measures.width > 4500 ? -2 : 0}`
        ]}
        rotation={locations['GABLE-END-BACK'].rotation}
        cover={gableEnd2.cover}
      />
    )
  }

  // SINGLE ALU (EXTERNAL) GABLE
  if(gableEnd.type === 'SINGLE_ALU_EXTERNAL') {
    if(measures.width === 2500) {
      parts.push(
        <SingleAluExternalGable_2500
          key={`single-alu-external-gable-2500`}
          position={[
            locations['GABLE-END'].position[0],
            locations['GABLE-END'].position[1] - `${foundation.type === 'FLAT_FEET' ? -liftFoundation : 0}`,
            locations['GABLE-END'].position[2]
          ]}
        />
      )
    } else {
      parts.push(
        <SingleAluExternalGable
          key={`single-alu-external-gable`}
          position={[
            locations['GABLE-END'].position[0],
            locations['GABLE-END'].position[1] - `${foundation.type === 'FLAT_FEET' ? -liftFoundation : 0}`,
            locations['GABLE-END'].position[2]
          ]}
        />
      )
    }
  }
  if(gableEnd2.type === 'SINGLE_ALU_EXTERNAL') {
    if(measures.width === 2500) {
      parts.push(
        <SingleAluExternalGable_2500
          key={`single-alu-external-gable-2500-2`}
          position={[
            locations['GABLE-END-BACK'].position[0] - widthToAdd,
            locations['GABLE-END-BACK'].position[1] - `${foundation.type === 'FLAT_FEET' ? -liftFoundation : 0}`,
            locations['GABLE-END-BACK'].position[2] + lengthToAdd
          ]}
          rotation={locations['GABLE-END-BACK'].rotation}
        />
      )
    } else {
      parts.push(
        <SingleAluExternalGable
          key={`single-alu-external-gable-2`}
          position={[
            locations['GABLE-END-BACK'].position[0] - widthToAdd,
            locations['GABLE-END-BACK'].position[1] - `${foundation.type === 'FLAT_FEET' ? -liftFoundation : 0}`,
            locations['GABLE-END-BACK'].position[2] + lengthToAdd
          ]}
          rotation={locations['GABLE-END-BACK'].rotation}
        />
      )
    }
  }

  // SINGLE ALU (INTERNAL) GABLE
  if(gableEnd.type === 'SINGLE_ALU_INTERNAL') {
    parts.push(
      <SingleAluInternalGable
        key={`single-alu-internal-gable`}
        position={[
          locations['GABLE-END'].position[0],
          locations['GABLE-END'].position[1] - `${foundation.type === 'FLAT_FEET' ? -liftFoundation : 0}`,
          locations['GABLE-END'].position[2]
        ]}
      />
    )
  }
  if(gableEnd2.type === 'SINGLE_ALU_INTERNAL') {
    parts.push(
      <SingleAluInternalGable
        key={`single-alu-internal-gable-2`}
        position={[
          locations['GABLE-END-BACK'].position[0] - widthToAdd,
          locations['GABLE-END-BACK'].position[1] - `${foundation.type === 'FLAT_FEET' ? -liftFoundation : 0}`,
          locations['GABLE-END-BACK'].position[2] + lengthToAdd - `${measures.width > 4500 ? -2 : 0}`
        ]}
        rotation={locations['GABLE-END-BACK'].rotation}
      />
    )
  }

  // DOUBLE ALU (INTERNAL) GABLE
  if(gableEnd.type === 'DOUBLE_ALU_INTERNAL') {
    parts.push(
      <DoubleAluInternalGable
        key={`double-alu-internal-gable`}
        position={[
          locations['GABLE-END'].position[0],
          locations['GABLE-END'].position[1] - `${foundation.type === 'FLAT_FEET' ? -liftFoundation : 0}`,
          locations['GABLE-END'].position[2]
        ]}
      />
    )
  }
  if(gableEnd2.type === 'DOUBLE_ALU_INTERNAL') {
    parts.push(
      <DoubleAluInternalGable
        key={`double-alu-internal-gable-2`}
        position={[
          locations['GABLE-END-BACK'].position[0] - widthToAdd,
          locations['GABLE-END-BACK'].position[1] - `${foundation.type === 'FLAT_FEET' ? -liftFoundation : 0}`,
          locations['GABLE-END-BACK'].position[2] + lengthToAdd - `${measures.width > 4500 ? -2 : 0}`
        ]}
        rotation={locations['GABLE-END-BACK'].rotation}
      />
    )
  }

  // SINGLE STEEL GABLE
  if(gableEnd.type === 'SINGLE_STEEL') {
    parts.push(
      <SingleSteelGable
        key={`single-steel-gable`}
        position={[
          locations['GABLE-END'].position[0],
          locations['GABLE-END'].position[1] - `${foundation.type === 'FLAT_FEET' ? -liftFoundation : 0}`,
          locations['GABLE-END'].position[2]
        ]}
        cover={gableEnd.cover}
      />
    )
  }
  if(gableEnd2.type === 'SINGLE_STEEL') {
    parts.push(
      <SingleSteelGable
        key={`single-steel-gable-2`}
        position={[
          locations['GABLE-END-BACK'].position[0] - widthToAdd,
          locations['GABLE-END-BACK'].position[1] - `${foundation.type === 'FLAT_FEET' ? -liftFoundation : 0}`,
          locations['GABLE-END-BACK'].position[2] + lengthToAdd
        ]}
        rotation={locations['GABLE-END-BACK'].rotation}
        cover={gableEnd2.cover}
      />
    )
  }

  // DOUBLE STEEL GABLE
  if(gableEnd.type === 'DOUBLE_STEEL') {
    parts.push(
      <DoubleSteelGable
        key={`double-steel-gable`}
        position={[
          locations['GABLE-END'].position[0],
          locations['GABLE-END'].position[1] - `${foundation.type === 'FLAT_FEET' ? -liftFoundation : 0}`,
          locations['GABLE-END'].position[2]
        ]}
        cover={gableEnd.cover}
      />
    )
  }
  if(gableEnd2.type === 'DOUBLE_STEEL') {
    parts.push(
      <DoubleSteelGable
        key={`double-steel-gable-2`}
        position={[
          locations['GABLE-END-BACK'].position[0] - widthToAdd,
          locations['GABLE-END-BACK'].position[1] - `${foundation.type === 'FLAT_FEET' ? -liftFoundation : 0}`,
          locations['GABLE-END-BACK'].position[2] + lengthToAdd - `${measures.width > 4500 ? -2 : 0}`
        ]}
        rotation={locations['GABLE-END-BACK'].rotation}
        cover={gableEnd2.cover}
      />
    )
  }

  // TIMBER GABLE
  if(gableEnd.type === 'SINGLE_TIMBER') {
    parts.push(
      <SingleTimberGable
        key={`single-timber-gable`}
        position={[
          locations['GABLE-END'].position[0],
          locations['GABLE-END'].position[1] - `${foundation.type === 'FLAT_FEET' ? -liftFoundation : 0}`,
          locations['GABLE-END'].position[2]
        ]}
        cover={gableEnd.cover}
      />
    )
  }
  if(gableEnd2.type === 'SINGLE_TIMBER') {
    parts.push(
      <SingleTimberGable
        key={`single-timber-gable-2`}
        position={[
          locations['GABLE-END-BACK'].position[0] - widthToAdd,
          locations['GABLE-END-BACK'].position[1] - `${foundation.type === 'FLAT_FEET' ? -liftFoundation : 0}`,
          locations['GABLE-END-BACK'].position[2] + lengthToAdd
        ]}
        rotation={locations['GABLE-END-BACK'].rotation}
        cover={gableEnd2.cover}
      />
    )
  }

  // DOUBLE TIMBER GABLE
  if(gableEnd.type === 'DOUBLE_TIMBER') {
    parts.push(
      <DoubleTimberGable
        key={`double-timber-gable`}
        position={[
          locations['GABLE-END'].position[0],
          locations['GABLE-END'].position[1] - `${foundation.type === 'FLAT_FEET' ? -liftFoundation : 0}`,
          locations['GABLE-END'].position[2]
        ]}
        cover={gableEnd.cover}
      />
    )
  }
  if(gableEnd2.type === 'DOUBLE_TIMBER') {
    parts.push(
      <DoubleTimberGable
        key={`double-timber-gable-2`}
        position={[
          locations['GABLE-END-BACK'].position[0] - widthToAdd,
          locations['GABLE-END-BACK'].position[1] - `${foundation.type === 'FLAT_FEET' ? -liftFoundation : 0}`,
          locations['GABLE-END-BACK'].position[2] + lengthToAdd - `${measures.width > 4500 ? -2 : 0}`
        ]}
        rotation={locations['GABLE-END-BACK'].rotation}
        cover={gableEnd2.cover}
      />
    )
  }

  // STEEL NO DOOR GABLE
  if(gableEnd.type === 'STEEL') {
    parts.push(
      <SteelNoDoorGable
        key={`steel-no-door`}
        position={[
          locations['GABLE-END'].position[0],
          locations['GABLE-END'].position[1] - `${foundation.type === 'FLAT_FEET' ? -liftFoundation : 0}`,
          locations['GABLE-END'].position[2]
        ]}
      />
    )  
  }
  if(gableEnd2.type === 'STEEL') {
    parts.push(
      <SteelNoDoorGable
        key={`steel-no-door-2`}
        position={[
          locations['GABLE-END-BACK'].position[0] - widthToAdd,
          locations['GABLE-END-BACK'].position[1] - `${foundation.type === 'FLAT_FEET' ? -liftFoundation : 0}`,
          locations['GABLE-END-BACK'].position[2] + lengthToAdd - `${measures.width > 4500 ? -2 : 0}`
        ]}
        rotation={locations['GABLE-END-BACK'].rotation}
      />
    )  
  }

  // HALF MOON GABLE
  if(gableEnd.type === 'HALF_MOON') {
    parts.push(
      <HalfMoonGable
        key={`half-moon`}
        position={[
          locations['GABLE-END'].position[0],
          locations['GABLE-END'].position[1] - `${foundation.type === 'FLAT_FEET' ? -liftFoundation : 0}`,
          locations['GABLE-END'].position[2]
        ]}
      />
    )  
  }
  if(gableEnd2.type === 'HALF_MOON') {
    parts.push(
      <HalfMoonGable
        key={`half-moon-2`}
        position={[
          locations['GABLE-END-BACK'].position[0] - widthToAdd,
          locations['GABLE-END-BACK'].position[1] - `${foundation.type === 'FLAT_FEET' ? -liftFoundation : 0}`,
          locations['GABLE-END-BACK'].position[2] + lengthToAdd - `${measures.width > 4500 ? -2 : 0}`
        ]}
        rotation={locations['GABLE-END-BACK'].rotation}
      />
    )  
  }

  // // END NO CUT
  // parts.push(
  //   <EndNoCut
  //     key={`end-no-cut`}
  //     position={[
  //       locations[measures.width]['END-NOCUT'].position[0],
  //       locations[measures.width]['END-NOCUT'].position[1],
  //       locations[measures.width]['END-NOCUT'].position[2] + lengthToAdd
  //     ]}
  //   />
  // )  

  // // STEEL GABLE
  // parts.push(
  //   <SteelGable
  //     key={`steel-gable-back`}
  //     position={[
  //       locations[measures.width]['STEEL-GABLE']['BACK'].position[0],
  //       locations[measures.width]['STEEL-GABLE']['BACK'].position[1],
  //       locations[measures.width]['STEEL-GABLE']['BACK'].position[2] + lengthToAdd
  //     ]}
  //   />
  // )

  // FADRIP IRRIGATION
  if(fadripIrrigation) {
    parts.push(
      <FadripIrrigation
        key={`fadrip-irrigation`}
        pipesToAdd={archsToAdd * 2 + (type !== 'HOBBY' ? 2 : 0)}
        linesNr={Math.floor(measures.width / 0.5) / 1000}
        moveIrrigation={moveIrrigation[measures.width]}
      />
    )
  }

  // OVERHEAD IRRIGATION
  if(overheadIrrigation) {
    parts.push(
      <OverheadIrrigation
        key={`overhead-irrigation`}
        position={[
          locations[measures.width]['OVERHEAD_IRRIGATION'].position[0],
          locations[measures.width]['OVERHEAD_IRRIGATION'].position[1] - `${foundation.type === 'FLAT_FEET' ? -liftFoundation : 0}`,
          locations[measures.width]['OVERHEAD_IRRIGATION'].position[2] + lengthToAdd
        ]}
        lengthToAdd={lengthToAdd}
        scale={scale(measures.length - 200, 3800)}
      />
    )
  }

  // CLADDING RAILS
  if(secure === "CLADDING") {
    parts.push(
      <Fragment key={`cladding-rails`}>
        <CladdingRailRight
          position={[
            locations['CLADDING-RAIL']['RIGHT'].position[0] - widthToAdd + widthFix[measures.width],
            locations['CLADDING-RAIL']['RIGHT'].position[1] - `${foundation.type === 'FLAT_FEET' ? -liftFoundation : 0}`,
            locations['CLADDING-RAIL']['RIGHT'].position[2]
          ]}
          rotation={locations['CLADDING-RAIL']['RIGHT'].rotation}
          railsNr={archsNr - 1}
          lengthToAdd={lengthToAdd}
        />
        <CladdingRailLeft
          position={[
            locations['CLADDING-RAIL']['LEFT'].position[0],
            locations['CLADDING-RAIL']['LEFT'].position[1] - `${foundation.type === 'FLAT_FEET' ? -liftFoundation : 0}`,
            locations['CLADDING-RAIL']['LEFT'].position[2]
          ]}
          rotation={locations['CLADDING-RAIL']['LEFT'].rotation}
          railsNr={archsNr - 1}
          lengthToAdd={lengthToAdd}
        />
      </Fragment>
    )
  }

  // SIDE VENT
  if(sideVent.side === 'RIGHT' || sideVent.side === 'BOTH') {
    parts.push(
      <SideVent
        key={`side-vent-right`}
        position={[
          locations['SIDE-VENT']['RIGHT'].position[0] - widthToAdd + widthFix[measures.width],
          locations['SIDE-VENT']['RIGHT'].position[1] - `${foundation.type === 'FLAT_FEET' ? -liftFoundation : 0}`,
          locations['SIDE-VENT']['RIGHT'].position[2]
        ]}
        rotation={locations['SIDE-VENT']['RIGHT'].rotation}
        lengthToAdd={lengthToAdd - `${measures.width > 4500 ? -2 : 0}`}
        railsToAdd={archsToAdd + 1 - `${measures.width > 4500 ? -1 : 0}`}
        gutterScale={scale(measures.length - 1000, 3000)}
        rollerScale={scale(measures.length - 354.01, 3645.99)}
        netNr={archsNr - 1}
        gutters={gutters === 'RIGHT' || gutters === 'BOTH'}
        fix={sideVentFix[measures.width] ? sideVentFix[measures.width] : 0}
      />
    )
  }
  if(sideVent.side === 'LEFT' || sideVent.side === 'BOTH') {
    parts.push(
      <SideVent
        key={`side-vent-left`}
        position={[
          locations['SIDE-VENT']['LEFT'].position[0],
          locations['SIDE-VENT']['LEFT'].position[1] - `${foundation.type === 'FLAT_FEET' ? -liftFoundation : 0}`,
          locations['SIDE-VENT']['LEFT'].position[2] + lengthToAdd - `${measures.width > 4500 ? -2 : 0}`
        ]}
        rotation={locations['SIDE-VENT']['LEFT'].rotation}
        lengthToAdd={lengthToAdd - `${measures.width > 4500 ? -2 : 0}`}
        railsToAdd={archsToAdd + 1 - `${measures.width > 4500 ? -1 : 0}`}
        gutterScale={scale(measures.length - 1000, 3000)}
        rollerScale={scale(measures.length - 354.01, 3645.99)}
        netNr={archsNr - 1}
        gutters={gutters === 'LEFT' || gutters === 'BOTH'}
        fix={sideVentFix[measures.width] ? sideVentFix[measures.width] : 0}
      />
    )
  }

  return (
    <group dispose={null}>
      {parts}
      {/* <PerspectiveCamera
        ref={ref}
        makeDefault
        fov={80}
        position={[-2.143, -0.861, -5]}
        // zoom={2}
        // fov={50}
        // position={[-5.56, 5.13, -5.5]}
      /> */}
      <Ground scale={[scale(measures.width + 4000, 6500), 1, scale(measures.length + 4000, 8000)]}/>
    </group>
  )
}

useGLTF.preload('/main.glb')

export default Model
